<script setup lang="ts">
import {onMounted, ref} from 'vue';
import ZZCMark from '@/views/header/ZZCMark.vue';
import TextButton from '@/views/ui/TextButton.vue';
import StatusTag from '@/views/ui/StatusTag.vue';
import {getAppVersion} from '@/base/frontendConfig';
import {tokenControle, wachtwoordZetten} from '@/base/auth.js';
import {WachtwoordAntwoord} from 'common';

type ReportState = { error: boolean, text?: string, next?: string };

// the mail puts the token in the fragment, so it never reaches a server log; the '#' itself is stripped first
const token: string = new URLSearchParams(window.location.hash.substring(1)).get('token') ?? '';
const newPassword   = ref<string>('');
const reportState   = ref<ReportState>(makeOk());
const linkGeldig    = ref<boolean | undefined>(undefined); // undefined while the link is being checked
let errorTimerId: number | ReturnType<typeof setTimeout>;

// a dead link is told on arrival, before a password is typed; expired, used and made-up links all read the same
onMounted(async () => {
    linkGeldig.value = await tokenControle(token);
});

function makeError(text: string, next?: string): ReportState {
    return {
        error: true,
        text,
        next,
    };
}

function makeOk(text?: string, next?: string): ReportState {
    return {
        error: false,
        text,
        next,
    };
}

function setReportState(state: ReportState) {
    clrReportState();
    reportState.value = state;
    if (state.text) {
        errorTimerId = setTimeout(clrReportState, 5000);
    } else {
        clrReportState();
    }
}

function clrReportState() {
    if (reportState.value.next) {
        window.location.assign(reportState.value.next);
    }
    reportState.value = makeOk();
    if (errorTimerId) {
        clearTimeout(errorTimerId);
        errorTimerId = 0;
    }
}

async function onResetPassword() {
    setReportState(await resetPassword());
}

async function resetPassword(): Promise<ReportState> {
    const antwoord: WachtwoordAntwoord = await wachtwoordZetten(token, newPassword.value);
    switch (antwoord.status) {
        case 'ok':
            return makeOk('Je wachtwoord is gezet', '/');
        case 'beleid':
            return makeError(antwoord.message ?? 'dit wachtwoord voldoet niet');
        case 'ongeldig':
            return makeError('ongeldig token', '/');
        default:
            console.error(antwoord);
            return makeError('er is een fout opgetreden', '/');
    }
}
</script>

<template>
    <div class="min-h-screen bg-zzc-lichtgrijs flex items-center justify-center p-6">
        <div
            @keydown.enter="onResetPassword"
            class="w-[360px] bg-white rounded-card border border-zzc-rand shadow-card p-7 flex flex-col gap-4"
        >
            <div class="flex items-center gap-3">
                <ZZCMark :size="36"/>
                <div class="flex flex-col leading-tight">
                    <span class="font-semibold text-zzc-blauw">Zeeuwse Zorgcoalitie</span>
                    <span class="text-[11px] text-zzc-muted">{{ getAppVersion() }}</span>
                </div>
            </div>
            <div class="text-lg font-semibold text-zzc-blauw">Wachtwoord kiezen</div>

            <div v-if="linkGeldig === false" class="flex flex-col items-center gap-4" data-testid="link-ongeldig">
                <StatusTag kind="bad">ongeldig token</StatusTag>
                <div class="text-sm text-zzc-muted">
                    Vraag op de inlogpagina een nieuwe aan met "Wachtwoord reset".
                </div>
                <a href="/" class="text-zzc-blauw hover:underline">naar de inlogpagina</a>
            </div>
            <div v-if="linkGeldig">
                <label for="newpassword" class="block text-xs font-medium text-zzc-muted">nieuw wachtwoord</label>
                <input
                    id="newpassword"
                    v-model="newPassword"
                    type="password"
                    class="mt-1 px-3 py-2 block w-full rounded-control border border-zzc-rand focus:border-zzc-blauw focus:outline-none focus:ring-2 focus:ring-[var(--el-color-primary-light-8)] disabled:text-zzc-muted disabled:bg-zzc-lichtgrijs"
                />
            </div>
            <div v-if="linkGeldig" class="flex flex-row justify-center items-center w-full h-8">
                <Transition
                    enter-from-class="opacity-0"
                    enter-active-class="transition-opacity duration-300 ease-out"
                    enter-to-class="opacity-100"
                    leave-from-class="opacity-100"
                    leave-active-class="transition-opacity duration-1000 ease-in"
                    leave-to-class="opacity-0"
                >
                    <StatusTag v-if="reportState.text" :kind="reportState.error ? 'bad' : 'ok'">
                        {{ reportState.text }}
                    </StatusTag>
                </Transition>
            </div>
            <div v-if="linkGeldig" class="flex items-center">
                <TextButton
                    variant="primary"
                    class="w-full justify-center"
                    @click="onResetPassword"
                >
                    Opslaan
                </TextButton>
            </div>
        </div>
    </div>
</template>